import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { getHoldings } from "../../services/portfolioService";

// Market configuration - same as import
const MARKETS = [
  {
    id: "US",
    name: "United States",
    suffix: "",
    currency: "USD",
    exchange: "NASDAQ",
  },
  {
    id: "RO",
    name: "Romania",
    suffix: ".RO",
    currency: "RON",
    exchange: "BVB",
  },
  {
    id: "DE",
    name: "Germany",
    suffix: ".DE",
    currency: "EUR",
    exchange: "XETRA",
  },
  {
    id: "UK",
    name: "United Kingdom",
    suffix: ".L",
    currency: "GBP",
    exchange: "LSE",
  },
  {
    id: "FR",
    name: "France",
    suffix: ".PA",
    currency: "EUR",
    exchange: "EURONEXT",
  },
  {
    id: "NL",
    name: "Netherlands",
    suffix: ".AS",
    currency: "EUR",
    exchange: "EURONEXT",
  },
  {
    id: "CH",
    name: "Switzerland",
    suffix: ".SW",
    currency: "CHF",
    exchange: "SIX",
  },
  { id: "JP", name: "Japan", suffix: ".T", currency: "JPY", exchange: "TSE" },
  {
    id: "HK",
    name: "Hong Kong",
    suffix: ".HK",
    currency: "HKD",
    exchange: "HKEX",
  },
];

const PortfolioExportPage = () => {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportFormat, setExportFormat] = useState("csv");

  useEffect(() => {
    fetchHoldings();
  }, []);

  const fetchHoldings = async () => {
    try {
      setLoading(true);
      const data = await getHoldings();
      setHoldings(data);
    } catch (error) {
      console.error("Error fetching holdings:", error);
      setError("Failed to load holdings");
    } finally {
      setLoading(false);
    }
  };

  const extractMarketFromTicker = (ticker, exchange) => {
    // Try to find market by suffix
    const market = MARKETS.find((m) => m.suffix && ticker.endsWith(m.suffix));
    if (market) {
      return market.id;
    }

    // Try to find by exchange
    const marketByExchange = MARKETS.find((m) => m.exchange === exchange);
    if (marketByExchange) {
      return marketByExchange.id;
    }

    // Default to US
    return "US";
  };

  const removeMarketSuffix = (ticker) => {
    // Remove known suffixes
    const suffixes = [".RO", ".DE", ".L", ".PA", ".AS", ".SW", ".T", ".HK"];
    for (const suffix of suffixes) {
      if (ticker.endsWith(suffix)) {
        return ticker.slice(0, -suffix.length);
      }
    }
    return ticker;
  };

  const exportToCSV = () => {
    const headers = [
      "Broker",
      "Market",
      "Ticker",
      "Company",
      "Type",
      "Quantity",
      "Price",
      "Date",
      "Currency",
      "Fees",
      "Notes",
    ];

    const rows = holdings.map((h) => {
      const market = extractMarketFromTicker(h.ticker_symbol, h.exchange);
      const baseTicker = removeMarketSuffix(h.ticker_symbol);

      return [
        h.broker_name || "",
        market,
        baseTicker,
        (h.company_name || "").replace(/,/g, ";"), // Replace commas to avoid CSV issues
        h.transaction_type,
        h.quantity,
        h.purchase_price,
        h.purchase_date,
        h.currency,
        h.fees || 0,
        (h.notes || "").replace(/,/g, ";").replace(/\n/g, " "),
      ].join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    downloadFile(
      csv,
      "text/csv",
      `portfolio-export-${new Date().toISOString().split("T")[0]}.csv`
    );
  };

  const exportToJSON = () => {
    const data = holdings.map((h) => {
      const market = extractMarketFromTicker(h.ticker_symbol, h.exchange);
      const baseTicker = removeMarketSuffix(h.ticker_symbol);

      return {
        broker: h.broker_name,
        market,
        ticker: baseTicker,
        fullTicker: h.ticker_symbol,
        company: h.company_name,
        exchange: h.exchange,
        transactionType: h.transaction_type,
        quantity: parseFloat(h.quantity),
        price: parseFloat(h.purchase_price),
        date: h.purchase_date,
        currency: h.currency,
        fees: parseFloat(h.fees || 0),
        notes: h.notes || "",
      };
    });

    const json = JSON.stringify(data, null, 2);
    downloadFile(
      json,
      "application/json",
      `portfolio-export-${new Date().toISOString().split("T")[0]}.json`
    );
  };

  const downloadFile = (content, mimeType, filename) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    if (exportFormat === "csv") {
      exportToCSV();
    } else {
      exportToJSON();
    }
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          <DownloadIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Export Portfolio
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
            {error}
          </Alert>
        )}

        {holdings.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            You don't have any transactions to export.
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
              <Typography variant="body2">
                <strong>Export includes Market column!</strong>
                <br />
                Your {holdings.length} transaction(s) will be exported with:
                <br />
                • Market code (US, RO, DE, etc.)
                <br />
                • Base ticker (without suffix)
                <br />
                • All transaction details
                <br />
                <br />
                You can re-import this file later, and the market suffix will be
                added automatically!
              </Typography>
            </Alert>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Export Format</FormLabel>
              <RadioGroup
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                <FormControlLabel
                  value="csv"
                  control={<Radio />}
                  label="CSV (Comma Separated Values) - Best for Excel/Sheets"
                />
                <FormControlLabel
                  value="json"
                  control={<Radio />}
                  label="JSON - Best for programming/data analysis"
                />
              </RadioGroup>
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {exportFormat === "csv"
                  ? "CSV format includes: Broker, Market, Ticker, Company, Type, Quantity, Price, Date, Currency, Fees, Notes"
                  : "JSON format includes all transaction data with market information"}
              </Typography>
            </Box>

            <Box mt={3}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                size="large"
              >
                Export {holdings.length} Transaction
                {holdings.length !== 1 ? "s" : ""} as{" "}
                {exportFormat.toUpperCase()}
              </Button>
            </Box>

            {exportFormat === "csv" && (
              <Box mt={3}>
                <Typography variant="body2" color="text.secondary">
                  <strong>CSV Example:</strong>
                  <br />
                  <code>
                    Broker,Market,Ticker,Company,Type,Quantity,Price,Date,Currency,Fees,Notes
                  </code>
                  <br />
                  <code>
                    XTB,US,AAPL,Apple
                    Inc.,BUY,10,150.50,2024-01-15,USD,9.99,First purchase
                  </code>
                  <br />
                  <code>
                    eToro,RO,SNN,OMV
                    Petrom,BUY,100,0.45,2024-01-20,RON,5.00,Romanian stock
                  </code>
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PortfolioExportPage;
